import { editComment } from '../api/taskApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchTasks,
  fetchTaskByIdAuth,
  createTask,
  updateTask,
  deleteTask,
  UpdateTaskData,
  addComment,
  addCommentWithImages,
  addReply,
  editReply,
  deleteReply,
  fetchComments,
  deleteComment,
  fetchFiles,
  uploadFile,
  deleteFile,
  CreateTaskData,
  // Subtask imports
  createSubtask,
  createSubtasksBulk,
  getSubtasks,
  getTaskWithSubtasks,
  updateSubtaskOrder,
  deleteSubtask,
  getTaskHierarchy,
  CreateSubtaskData,
} from '../api/taskApi';

// Fetch all tasks (no auth)
// By default, only fetch user's own/assigned tasks. Pass showAll=true to see all (if allowed)
export const useTasks = (showAll: boolean = false) => {
  return useQuery({
    queryKey: ['tasks', { showAll }],
    queryFn: () => fetchTasks(showAll),
  });
};

// Fetch single task by ID (with auth)
export const useTask = (id: string) => {
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: async () => {
      const res = await fetchTaskByIdAuth(id);
      return res.data;
    },
    enabled: !!id,
  });
};

// Comments hooks
export const useTaskComments = (id: string) => {
  return useQuery({
    queryKey: ['tasks', id, 'comments'],
    queryFn: async () => {
      const res = await fetchComments(id);
      return res.data;
    },
    enabled: !!id,
  });
};
export const useAddComment = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ comment, files, images }: { comment: string; files?: File[]; images?: string[] }) => {
      let res;
      if (images && images.length > 0) {
        // Use the new Cloudinary-based API
        res = await addCommentWithImages(id, comment, images);
      } else {
        // Use the old file-based API
        res = await addComment(id, comment, files);
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id, 'comments'] });
    },
  });
};
// Edit comment mutation
export const useEditComment = (taskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, text }: { commentId: string; text: string }) => {
      const res = await editComment(taskId, commentId, text);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
    },
  });
};
export const useDeleteComment = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (commentId: string) => {
      const res = await deleteComment(id, commentId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id, 'comments'] });
    },
  });
};

// Files hooks
export const useTaskFiles = (id: string) => {
  return useQuery({
    queryKey: ['tasks', id, 'files'],
    queryFn: async () => {
      const res = await fetchFiles(id);
      return res.data;
    },
    enabled: !!id,
  });
};
export const useUploadFile = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const res = await uploadFile(id, file);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id, 'files'] });
    },
  });
};
export const useDeleteFile = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await deleteFile(id, fileId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id, 'files'] });
    },
  });
};

// Create task mutation
export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Update task mutation
export const useUpdateTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskData }) => updateTask(id, data),
    onSuccess: updatedTask => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', updatedTask._id] });
    },
  });
};

// Delete task mutation
export const useDeleteTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Reply hooks
export const useAddReply = (taskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, text, images, parentReplyId }: { commentId: string; text: string; images?: string[]; parentReplyId?: string }) => {
      const res = await addReply(taskId, commentId, text, images, parentReplyId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
    },
  });
};

export const useDeleteReply = (taskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, replyId }: { commentId: string; replyId: string }) => {
      const res = await deleteReply(taskId, commentId, replyId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
    },
  });
};

export const useEditReply = (taskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, replyId, text }: { commentId: string; replyId: string; text: string }) => {
      const res = await editReply(taskId, commentId, replyId, text);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId, 'comments'] });
    },
  });
};

// ===============================
// SUBTASK HOOKS
// ===============================

// Get all subtasks for a parent task
export const useSubtasks = (parentTaskId: string) => {
  return useQuery({
    queryKey: ['tasks', parentTaskId, 'subtasks'],
    queryFn: async () => {
      const res = await getSubtasks(parentTaskId);
      return res.data;
    },
    enabled: !!parentTaskId,
  });
};

// Get a task with all its subtasks populated
export const useTaskWithSubtasks = (taskId: string) => {
  return useQuery({
    queryKey: ['tasks', taskId, 'with-subtasks'],
    queryFn: async () => {
      const res = await getTaskWithSubtasks(taskId);
      return res.data;
    },
    enabled: !!taskId,
  });
};

// Get task hierarchy (parent with all subtasks and metadata)
export const useTaskHierarchy = (taskId: string) => {
  return useQuery({
    queryKey: ['tasks', taskId, 'hierarchy'],
    queryFn: async () => {
      const res = await getTaskHierarchy(taskId);
      return res.data;
    },
    enabled: !!taskId,
  });
};

// Create a subtask
export const useCreateSubtask = (parentTaskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSubtaskData) => {
      const res = await createSubtask(parentTaskId, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'subtasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'with-subtasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useCreateSubtasksBulk = (parentTaskId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subtasks: CreateSubtaskData[]) => {
      const res = await createSubtasksBulk(parentTaskId, subtasks);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'subtasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', parentTaskId, 'with-subtasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

// Update subtask order
export const useUpdateSubtaskOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ subtaskId, newOrder }: { subtaskId: string; newOrder: number }) => {
      const res = await updateSubtaskOrder(subtaskId, newOrder);
      return res.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.subtaskId] });
    },
  });
};

// Delete a subtask
export const useDeleteSubtask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (subtaskId: string) => {
      const res = await deleteSubtask(subtaskId);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};
